import {
  Box,
  Divider,
  Flex,
  Grid,
  Text,
  VStack,
} from '@chakra-ui/react'
import {
  Facebook,
  Instagram,
  Marker,
  Tripadvisor,
  Twitter,
  Youtube,
} from '@icon'
import { Link } from '@ui'
import useTranslation from 'next-translate/useTranslation'
import { useRouter } from 'next/router'
import NAV_LINKS from './FooterNav.const'
import { createNavLink } from '@utils'
import { IFonts } from '@interfaces'

interface IFooterNav {
  activeFontFamily?: IFonts
}

const FooterNav = ({ activeFontFamily: _activeFontFamily = 'body' }: IFooterNav) => {
  const { t } = useTranslation()
  const { locale } = useRouter()
  const footerFontFamily = "'Duru Sans','Duru Sans Regular',sans-serif"
  const contactLabelStyles = {
    color: 'black.primary3',
    fontSize: '13px',
    letterSpacing: '0.04em',
    textTransform: 'uppercase' as const,
  }

  return (
    <Box
      as="nav"
      width="full"
      bg="white.primary"
      fontFamily={footerFontFamily}
      sx={{
        '&, & *': {
          fontFamily: `${footerFontFamily} !important`,
          fontWeight: '400 !important',
        },
      }}
      pt={{ base: 'padding.primary', lg: '80px' }}
      pb="0"
      px={{ base: 'padding.primary', lg: 'padding.primary2' }}
    >
      <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={{ base: 3, lg: 3 }}>
        {/* Left Section: Contact and Socials */}
        <Flex
          flexDir="column"
          justifyContent="space-between"
          maxW={{ base: 'full', lg: '320px', xl: 'full' }}
        >
          <Box>
            <Text
              as="h3"
              color="red.primary"
              fontSize={{ base: 'xl', lg: '2xl' }}
              fontFamily={footerFontFamily}
              mb={10}
              fontWeight="regular"
            >
              {t('common:siteFullName')}
            </Text>
            
            <VStack align="stretch" spacing={2} mb={8}>
              <Text color="black.primary3" fontSize="sm" fontWeight="regular">
                <Text as="span" {...contactLabelStyles}>
                  {t('common:contact.address')}:{' '}
                </Text>
                Sakıp Sabancı Caddesi No:42 Emirgan 34467 İstanbul
              </Text>
              <Text color="black.primary3" fontSize="sm" fontWeight="regular">
                <Text as="span" {...contactLabelStyles}>
                  {t('common:contact.tel')}:{' '}
                </Text>
                <Text
                  as="a"
                  href="tel:+90 212 277 22 00"
                  _hover={{ color: 'red.primary' }}
                >
                  +90 212 277 22 00
                </Text>
              </Text>
              <Text color="black.primary3" fontSize="sm" fontWeight="regular">
                <Text as="span" {...contactLabelStyles}>
                  {t('common:contact.email')}:{' '}
                </Text>
                <Text
                  as="a"
                  href="mailto:ssminfo@sabanciuniv.edu"
                  _hover={{ color: 'red.primary' }}
                >
                  ssminfo@sabanciuniv.edu
                </Text>
              </Text>
            </VStack>

            <Link
              href="https://goo.gl/maps/L3KzMMhPLn6CKmF39"
              target="_blank"
              display="flex"
              flexDirection="row"
              alignItems="center"
              color="red.primary"
              mb={12}
              fontSize="sm"
              fontWeight="regular"
              _hover={{ color: 'red.primary3' }}
            >
              <Marker />
              <Text ml="8px">
                {t('common:contact.getDirections')}
              </Text>
            </Link>
          </Box>

          <Flex h={{ base: 'auto', lg: '100px' }} gap={6} alignItems="center" mt={{ base: 10, lg: 0 }}>
            <Link href={NAV_LINKS.socialMedia.facebook} target="_blank" color="grey.primary" _hover={{ color: 'red.primary' }}>
              <Facebook />
            </Link>
            <Link href={NAV_LINKS.socialMedia.youtube} target="_blank" color="grey.primary" _hover={{ color: 'red.primary' }}>
              <Youtube />
            </Link>
            <Link href={NAV_LINKS.socialMedia.instagram} target="_blank" color="grey.primary" _hover={{ color: 'red.primary' }}>
              <Instagram />
            </Link>
            <Link href={NAV_LINKS.socialMedia.tripadvisor} target="_blank" color="grey.primary" _hover={{ color: 'red.primary' }}>
              <Tripadvisor />
            </Link>
            <Link href={NAV_LINKS.socialMedia.twitter} target="_blank" color="grey.primary" _hover={{ color: 'red.primary' }}>
              <Twitter />
            </Link>
          </Flex>
        </Flex>

        {/* Right Section: Navigation Links and Vertical Divider */}
        <Flex>
          <Divider
            orientation="vertical"
            borderColor="white.primary3"
            mr={12}
            display={{ base: 'none', lg: 'block' }}
          />
          <Flex flexDir="column" justifyContent="space-between" width="full">
            <Grid
              templateColumns={{ base: '1fr', md: '1fr 1fr' }}
              gap={{ base: 3, md: 8 }}
              width="full"
              alignItems="flex-start"
              pt={2}
            >
              <VStack align="stretch" spacing={{ base: 2, md: 3 }}>
                {NAV_LINKS.navigation.leftCol.map((nav) => (
                  <Link
                    key={nav.id}
                    href={createNavLink(nav.path, locale)}
                    target={nav.isExternal ? '_blank' : '_self'}
                    fontSize={{ base: 'sm', md: 'sm' }}
                    fontWeight="regular"
                    color="black.primary3"
                    _hover={{ color: 'red.primary' }}
                  >
                    {t(nav.labelTx)}
                  </Link>
                ))}
              </VStack>
              <VStack align="stretch" spacing={{ base: 2, md: 3 }}>
                {NAV_LINKS.navigation.rightCol.map((nav) => (
                  <Link
                    key={nav.id}
                    href={createNavLink(nav.path, locale)}
                    target={nav.isExternal ? '_blank' : '_self'}
                    fontSize={{ base: 'sm', md: 'sm' }}
                    fontWeight="regular"
                    color="black.primary3"
                    _hover={{ color: 'red.primary' }}
                  >
                    {t(nav.labelTx)}
                  </Link>
                ))}
              </VStack>
            </Grid>

            {/* Legal Links aligned with Social Icons (vertically centered to button) */}
            <Flex
              h={{ base: 'auto', lg: '100px' }}
              mt={{ base: 10, lg: 0 }}
              width="full"
              justifyContent={{ base: 'center', lg: 'flex-end' }}
              columnGap={{ base: 4, lg: 6 }}
              rowGap={{ base: 2, lg: 2 }}
              flexWrap="wrap"
              alignItems="center"
              alignContent="center"
            >
              {NAV_LINKS.navigation.bottomRow.map((nav) => (
                <Link
                  key={nav.id}
                  href={createNavLink(nav.path, locale)}
                  fontSize="xs"
                  lineHeight="short"
                  color="grey.primary"
                  _hover={{ color: 'red.primary' }}
                >
                  {t(nav.labelTx)}
                </Link>
              ))}
            </Flex>
          </Flex>
        </Flex>
      </Grid>
    </Box>
  )
}

export default FooterNav